import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type DtefPaymentDetail,
  type PixDtefDetail,
} from "@/services/conciliation/dtfe-import";
import { ChevronDown, ChevronRight, Loader2, Search, X } from "lucide-react";

interface BatchDetailsModalProps {
  dtef_payment: DtefPaymentDetail[];
  pix_dtef: PixDtefDetail[];
  isLoading: boolean;
  type: "pix" | "payment";
}

interface StoreGroup {
  store_code: number;
  rows: DtefPaymentDetail[];
  totalQuantity: number;
  totalAmount: number;
}

function groupByStore(data: DtefPaymentDetail[]): StoreGroup[] {
  const map = new Map<number, StoreGroup>();

  for (const row of data) {
    let group = map.get(row.store_code);
    if (!group) {
      group = { store_code: row.store_code, rows: [], totalQuantity: 0, totalAmount: 0 };
      map.set(row.store_code, group);
    }
    group.rows.push(row);
    group.totalQuantity += row.quantity;
    const numVal = parseFloat(
      row.total_amount.replace(/\./g, "").replace(",", "."),
    );
    group.totalAmount += isNaN(numVal) ? 0 : numVal;
  }

  return Array.from(map.values()).sort((a, b) => a.store_code - b.store_code);
}

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const BatchDetailsModal = ({
  dtef_payment,
  pix_dtef,
  isLoading,
  type,
}: BatchDetailsModalProps) => {
  const [expandedStores, setExpandedStores] = useState<Set<number>>(new Set());
  const [storeSearch, setStoreSearch] = useState("");

  const storeGroups = useMemo(() => groupByStore(dtef_payment), [dtef_payment]);

  const filteredStoreGroups = useMemo(() => {
    const term = storeSearch.trim();
    if (!term) return storeGroups;
    return storeGroups.filter(g => String(g.store_code).includes(term));
  }, [storeGroups, storeSearch]);

  const filteredPixRows = useMemo(() => {
    const term = storeSearch.trim();
    if (!term) return pix_dtef;
    return pix_dtef.filter(r => String(r.store_code).includes(term));
  }, [pix_dtef, storeSearch]);

  const toggleStore = (storeCode: number) => {
    setExpandedStores(prev => {
      const next = new Set(prev);
      if (next.has(storeCode)) next.delete(storeCode);
      else next.add(storeCode);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedStores(new Set(filteredStoreGroups.map(g => g.store_code)));
  };

  const collapseAll = () => {
    setExpandedStores(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-[#613F4F]" />
        <span className="ml-2 text-sm text-gray-500">
          Carregando detalhes...
        </span>
      </div>
    );
  }

  const isEmpty =
    (type === "payment" && dtef_payment.length === 0) ||
    (type === "pix" && pix_dtef.length === 0);

  if (isEmpty) {
    return (
      <p className="text-sm text-gray-500 text-center py-4">
        Nenhum registro ativo encontrado para este lote.
      </p>
    );
  }

  const totalStores =
    type === "payment"
      ? storeGroups.length
      : new Set(pix_dtef.map(r => r.store_code)).size;
  const totalQty =
    type === "payment"
      ? storeGroups.reduce((s, g) => s + g.totalQuantity, 0)
      : pix_dtef.reduce((s, r) => s + r.quantity, 0);
  const totalAmt =
    type === "payment"
      ? storeGroups.reduce((s, g) => s + g.totalAmount, 0)
      : 0;

  const isFiltering = storeSearch.trim().length > 0;
  const visibleStoreCount =
    type === "payment" ? filteredStoreGroups.length : filteredPixRows.length;

  return (
    <div className="flex flex-col gap-4 min-w-[700px]">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500">Lojas</p>
          <p className="text-lg font-semibold text-[#613F4F]">{totalStores}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500">Registros</p>
          <p className="text-lg font-semibold text-[#613F4F]">
            {totalQty.toLocaleString("pt-BR")}
          </p>
        </div>
        {type === "payment" && (
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-500">Valor Total</p>
            <p className="text-lg font-semibold text-[#613F4F]">
              R$ {formatCurrency(totalAmt)}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Buscar loja por código..."
            value={storeSearch}
            onChange={e => setStoreSearch(e.target.value)}
            className="pl-9 h-9 border-[#613F4F]/20 text-sm"
          />
          {storeSearch && (
            <button
              type="button"
              onClick={() => setStoreSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Limpar busca"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {isFiltering && (
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {visibleStoreCount}{" "}
            {visibleStoreCount === 1 ? "loja" : "lojas"}
          </span>
        )}
      </div>

      {type === "payment" && (
        <>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={expandAll}
            >
              Expandir tudo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={collapseAll}
            >
              Recolher tudo
            </Button>
          </div>

          <div className="rounded-md border max-h-[450px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>Loja</TableHead>
                  <TableHead>Rede</TableHead>
                  <TableHead>Bandeira</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-right">Qtd</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStoreGroups.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="h-24 text-center text-sm text-gray-500"
                    >
                      Nenhuma loja encontrada para "{storeSearch}".
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStoreGroups.map(group => {
                    const isExpanded = expandedStores.has(group.store_code);
                    return (
                      <>
                        <TableRow
                          key={`store-${group.store_code}`}
                          className="bg-gray-50 cursor-pointer hover:bg-gray-100"
                          onClick={() => toggleStore(group.store_code)}
                        >
                          <TableCell className="py-2">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-[#613F4F]" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-[#613F4F]" />
                            )}
                          </TableCell>
                          <TableCell className="py-2 font-semibold text-[#613F4F]">
                            Loja {group.store_code}
                          </TableCell>
                          <TableCell
                            colSpan={3}
                            className="py-2 text-sm text-gray-500"
                          >
                            {group.rows.length} registros agrupados
                          </TableCell>
                          <TableCell className="py-2 text-right font-semibold">
                            {group.totalQuantity}
                          </TableCell>
                          <TableCell className="py-2 text-right font-semibold text-[#613F4F]">
                            R$ {formatCurrency(group.totalAmount)}
                          </TableCell>
                          <TableCell className="py-2" />
                        </TableRow>
                        {isExpanded &&
                          group.rows.map((row, i) => (
                            <TableRow key={`${group.store_code}-${i}`}>
                              <TableCell />
                              <TableCell className="text-gray-400">
                                {row.store_code}
                              </TableCell>
                              <TableCell>{row.authorization}</TableCell>
                              <TableCell>{row.brand}</TableCell>
                              <TableCell>{row.type_operation}</TableCell>
                              <TableCell className="text-right">
                                {row.quantity}
                              </TableCell>
                              <TableCell className="text-right">
                                R$ {row.total_amount}
                              </TableCell>
                              <TableCell>{row.date_operation}</TableCell>
                            </TableRow>
                          ))}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {type === "pix" && (
        <div className="rounded-md border max-h-[450px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Loja</TableHead>
                <TableHead className="text-right">Qtd</TableHead>
                <TableHead className="text-right">Valor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPixRows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className="h-24 text-center text-sm text-gray-500"
                  >
                    Nenhuma loja encontrada para "{storeSearch}".
                  </TableCell>
                </TableRow>
              ) : (
                filteredPixRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">
                      {row.store_code}
                    </TableCell>
                    <TableCell className="text-right">{row.quantity}</TableCell>
                    <TableCell className="text-right">
                      R$ {row.total_amount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
